import React, { useState, useEffect } from "react";
import {
  Sparkles,
  Clock,
  BookOpen,
  ChefHat,
  Gift,
  Check,
  Users,
  Award,
  ShieldCheck,
  ArrowRight,
  Lock,
  X,
  ChevronDown,
  ChevronUp,
  Flame,
  Star,
  Info,
  Percent,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { Toaster, toast } from "@/components/ui/sonner";

export default function App() {
  // State for pop-up form
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  
  // State for submission (persisted in localStorage for better UX)
  const [isSubmitted, setIsSubmitted] = useState(() => {
    return localStorage.getItem("chef_ashmika_is_submitted") === "true";
  });
  
  // Form values
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem("chef_ashmika_user_data");
      return saved ? JSON.parse(saved) : { name: "", whatsapp: "", email: "" };
    } catch (e) {
      return { name: "", whatsapp: "", email: "" };
    }
  });

  // Interactive Curriculum Accordion
  const [expandedModule, setExpandedModule] = useState(0);
  // Bakes gallery tab switcher
  const [activeTab, setActiveTab] = useState("brownies");
  // Interactive FAQ Accordion
  const [expandedFaq, setExpandedFaq] = useState(null);
  // Sticky footer visibility on scroll
  const [showStickyFooter, setShowStickyFooter] = useState(false);

  // Countdown timer state
  const [timeLeft, setTimeLeft] = useState({ hours: 14, minutes: 25, seconds: 40 });

  // Auto popup trigger after 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only open if they haven't submitted yet
      const currentlySubmitted = localStorage.getItem("chef_ashmika_is_submitted") === "true";
      if (!isSubmitted && !currentlySubmitted) {
        setIsPopupOpen(true);
        // Clear previous toasts to avoid stacking
        toast.dismiss();
        toast.info("⏳ Reserve your seat before registration closes!", {
          description: "Chef Ashmika's Masterclass has limited slots.",
          duration: 4000,
        });
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [isSubmitted]);

  // Countdown countdown effect
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else {
          return { hours: 14, minutes: 25, seconds: 40 }; // Reset loop for urgency
        }
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Sticky footer visible on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 500) {
        setShowStickyFooter(true);
      } else {
        setShowStickyFooter(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Escape key support to close popup
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsPopupOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handle Input Changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle Form Submit
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.whatsapp || !formData.email) {
      toast.error("Please fill in all the details.");
      return;
    }

    // Phone / WhatsApp basic digits-only validation (excluding country codes)
    const rawDigits = formData.whatsapp.replace(/[^0-9]/g, "");
    if (rawDigits.length < 8) {
      toast.error("Invalid WhatsApp Number", {
        description: "Please enter a valid WhatsApp number with country code.",
      });
      return;
    }

    // Save lead in localStorage so they are captured safely on client side
    const currentLeads = JSON.parse(localStorage.getItem("chef_ashmika_leads") || "[]");
    const updatedLeads = [...currentLeads, { ...formData, timestamp: new Date().toISOString() }];
    localStorage.setItem("chef_ashmika_leads", JSON.stringify(updatedLeads));

    // Save submission status & user details
    localStorage.setItem("chef_ashmika_is_submitted", "true");
    localStorage.setItem("chef_ashmika_user_data", JSON.stringify(formData));

    // Dismiss any informational toasts before showing success screen
    toast.dismiss();

    // Update state
    setIsSubmitted(true);
    setIsPopupOpen(false);
    toast.success("✨ Registration Successful!", {
      description: "Welcome to Chef Ashmika's Masterclass community.",
    });
  };

  // Reset function to clear submission (useful for testing or starting over)
  const handleResetRegistration = () => {
    localStorage.removeItem("chef_ashmika_is_submitted");
    localStorage.removeItem("chef_ashmika_user_data");
    setIsSubmitted(false);
    setFormData({ name: "", whatsapp: "", email: "" });
    toast.info("Registration reset. You can now test the form again!");
  };

  // Curriculum Modules list
  const curriculumModules = [
    {
      title: "Module 1: Understand the Fundamentals",
      icon: <Info className="w-5 h-5 text-gold-burnished" />,
      bullets: [
        "Introduction to millets and their nutritional properties",
        "Different types of millets and their applications in baking",
        "Understanding gluten and its role in baking",
        "Gluten-free baking fundamentals",
        "Understanding millet flours and their behaviour",
        "How to achieve the right texture in millet brownies and blondies",
        "Understanding layering, fillings and inclusions",
        "Troubleshooting common brownie and blondie problems"
      ]
    },
    {
      title: "Module 2: Premium Layered Brownie & Blondie Collection",
      icon: <Sparkles className="w-5 h-5 text-gold-burnished" />,
      bullets: [
        "🥮 Pistachio Baklava Brownie - Rich, multi-textured, gold-leaf trimmed",
        "💚 Dubai Pistachio Brownie & Pistachio Kunafa Brownie",
        "🍊 Orange Chocolate Brownie with premium inclusions",
        "🤎 Biscoff Brownie & Honey Salted Caramel Brownie",
        "🌰 Hazelnut Praline Brownie & Chocolate Nut Crunch Brownie",
        "🌹 Rose & Pistachio Blondie",
        "🌰 Cashew White Chocolate Blondie",
        "🫐 Mixed Berry Blondie & Cookie brownie Blondie",
        "Master the art of combining premium textures and layers effortlessly"
      ]
    },
    {
      title: "Module 3: Single-Serve Brownies & Blondies",
      icon: <BookOpen className="w-5 h-5 text-gold-burnished" />,
      bullets: [
        "✨ Single-Serve Brownies - individually baked perfectly sized treats",
        "✨ Single-Serve Blondies - gorgeous vanilla bean based single portions",
        "✨ Individual Layered Dessert Portions with premium toppings",
        "Perfect for dessert boxes, premium gifting, party orders, and premium bakery menus"
      ]
    },
    {
      title: "Module 4: Brownie & Blondie 2.0 (The Advanced Science)",
      icon: <ChefHat className="w-5 h-5 text-gold-burnished" />,
      bullets: [
        "Understanding brownie & blondie textures (fudgy vs cakey vs chewy)",
        "Layering techniques and custom flavour combinations",
        "Working with different fillings and crunchy elements (kunafa, pralines)",
        "Balancing sweetness, fat, and hydration in millet flour recipes",
        "Understanding exact baking temperatures and convection techniques",
        "Cutting, portioning, and achieving perfectly clean premium layers",
        "Gourmet finishing and gold dust presentation techniques"
      ]
    },
    {
      title: "Module 5: Turn Your Recipes into Premium Products",
      icon: <Award className="w-5 h-5 text-gold-burnished" />,
      bullets: [
        "💰 Product Costing & Profit Margins (maximize profit margins up to 75%)",
        "📦 Luxury Packaging & Premium Presentation",
        "📋 Storage & Shelf Life Management guidelines",
        "❄️ Refrigerated & Room Temperature Storage specifics",
        "📈 Creating Your Own Premium Brownie & Blondie Menu",
        "🎁 Luxury Gifting Boxes and curated menu ranges"
      ]
    },
    {
      title: "Module 6: Storage & Shelf-Life Validation",
      icon: <ShieldCheck className="w-5 h-5 text-gold-burnished" />,
      bullets: [
        "❄️ Refrigerated Shelf Life: Approximately 1 month (ideal targets)",
        "🌡️ Room Temperature: Approximately 1 week",
        "Learn how actual shelf life depends on fillings, moisture, hygiene, and packaging",
        "Comprehensive shelf-life validation protocols for professional home bakers"
      ]
    }
  ];

  // FAQ List
  const faqList = [
    {
      question: "Is this masterclass suitable for beginners?",
      answer: "Absolutely! We start with gluten-free and millet fundamentals before moving to the premium layered collection. Chef Ashmika explains the science so clearly that anyone can achieve bakery-style layers on their first attempt."
    },
    {
      question: "Will I get written recipes?",
      answer: "Yes, you will receive a comprehensive, detailed Recipe PDF containing exact gram-measurements, temperature details, ingredient sourcing links, and packaging blueprints."
    },
    {
      question: "Do I need any professional kitchen equipment?",
      answer: "No professional equipment is needed. A standard OTG or microwave convection oven, a baking pan, parchment paper, and basic bowls are more than enough to create these masterclass recipes."
    },
    {
      question: "Will the session recording be shared?",
      answer: "Yes! Every registered student gets lifetime access to the masterclass recording so you can rewatch and master the layers at your own convenience."
    }
  ];

  return (
    <div className="min-h-screen bg-chocolate-deep text-cream-warm font-sans antialiased selection:bg-gold-burnished selection:text-chocolate-deep">
      <Toaster position="bottom-right" />

      {/* 01. VIP Urgent Scarcity Header Ticker */}
      <div 
        data-testid="header-ticker"
        className="bg-gradient-to-r from-chocolate-surface via-chocolate-deep to-chocolate-surface border-b border-chocolate-milk/20 py-2.5 px-4 text-center sticky top-0 z-40 backdrop-blur-md bg-opacity-95"
      >
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 text-xs sm:text-sm">
          <span className="inline-flex items-center gap-1.5 text-chocolate-milk font-semibold tracking-wider uppercase">
            <Flame className="w-4 h-4 text-gold-burnished animate-pulse" /> Limited Seats Remaining!
          </span>
          <span className="text-cream-soft">
            Chef Ashmika's Live Masterclass registration closes soon! Time remaining:
          </span>
          <span className="bg-chocolate-elevated text-cream-warm px-2 py-0.5 rounded font-mono font-bold tracking-wider border border-chocolate-milk/15">
            {timeLeft.hours.toString().padStart(2, "0")}h : {timeLeft.minutes.toString().padStart(2, "0")}m : {timeLeft.seconds.toString().padStart(2, "0")}s
          </span>
          <span className="hidden lg:inline text-chocolate-milk font-semibold">• Only 6 spots left today!</span>
        </div>
      </div>

      {/* Main Container or Thank You Page View */}
      {isSubmitted ? (
        /* Thank You View Screen */
        <main className="min-h-[90vh] flex items-center justify-center py-16 px-4 bg-radial-cocoa">
          <div className="max-w-xl w-full bg-chocolate-elevated/95 border-2 border-chocolate-milk/30 rounded-3xl p-8 sm:p-12 shadow-[0_15px_40px_rgba(43,26,18,0.06)] text-center relative overflow-hidden">
            {/* Absolute background gold shimmer lines */}
            <div className="absolute inset-0 bg-gradient-to-b from-gold-burnished/5 to-transparent pointer-events-none" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-chocolate-surface border-2 border-chocolate-milk/30 rounded-full mb-6 text-cream-warm">
                <CheckCircle2 className="w-10 h-10 text-chocolate-milk" />
              </div>
              
              <h1 className="text-3xl sm:text-4xl font-serif text-cream-warm tracking-tight mb-4">
                Registration Confirmed!
              </h1>
              
              <div className="h-[2px] bg-gradient-to-r from-transparent via-chocolate-milk/30 to-transparent my-6" />
              
              <p className="text-chocolate-milk text-xs uppercase tracking-widest font-semibold mb-2">
                Welcome, {formData.name || "Baker"}!
              </p>
              
              <p className="text-cream-soft text-base mb-8 leading-relaxed font-medium">
                Thank you! Please join the student WhatsApp group below.
              </p>

              {/* WhatsApp Call to Action */}
              <a 
                href="https://chat.whatsapp.com/BoSSLldavrgAByADwibHU0?s=cl&p=i&mlu=4&ilr=4&iam=2"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="thankyou-join-whatsapp-button"
                className="inline-flex items-center justify-center gap-3 w-full px-8 py-5 bg-gradient-to-r from-emerald-700 to-green-600 hover:from-emerald-600 hover:to-green-500 text-white font-bold text-lg sm:text-xl rounded-2xl shadow-[0_12px_30px_rgba(16,185,129,0.25)] hover:shadow-[0_16px_40px_rgba(16,185,129,0.4)] transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
              >
                <Users className="w-6 h-6 animate-bounce" />
                Join Student WhatsApp Group
              </a>
              
              <div className="mt-8 pt-6 border-t border-chocolate-milk/15 flex justify-center">
                <button
                  onClick={handleResetRegistration}
                  data-testid="thankyou-reset-button"
                  className="text-xs text-muted-amber hover:text-cream-warm transition-colors underline"
                >
                  Need to change your details or test again? Click here
                </button>
              </div>
            </div>
          </div>
        </main>
      ) : (
        /* Regular Marketing Landing Page */
        <>
          {/* 02. Decadent Hero Section */}
          <header className="relative py-16 lg:py-24 border-b border-chocolate-milk/10 overflow-hidden bg-radial-cocoa">
            {/* Soft decorative background effects */}
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-chocolate-milk/5 rounded-full blur-[120px] pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                
                {/* Left Side: Copy */}
                <div className="lg:col-span-7 text-left space-y-6">
                  <div className="inline-flex items-center gap-2 bg-chocolate-surface border border-chocolate-milk/20 px-3.5 py-1.5 rounded-full text-cream-soft text-xs uppercase tracking-widest font-semibold">
                    <Sparkles className="w-3.5 h-3.5" /> Chef Ashmika's Mango Studio Present
                  </div>
                  
                  <h1 
                    data-testid="hero-title"
                    className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight text-cream-warm leading-tight uppercase"
                  >
                    Millet Brownies and Blondies Masterclass
                  </h1>

                  <p className="text-lg sm:text-xl font-medium text-chocolate-milk italic">
                    What if your brownies could be more than just brownies? 🤎
                  </p>

                  <p className="text-base sm:text-lg text-cream-soft leading-relaxed max-w-2xl font-medium">
                    Learn to create rich, fudgy, multi-layered brownies and indulgent blondies with delicious fillings, crunchy inclusions, creamy layers, and premium flavor combinations. Elevate your menu with international dessert-inspired bakes!
                  </p>

                  <div className="space-y-3 py-2">
                    <div className="flex items-center gap-2.5 text-cream-soft font-medium">
                      <Check className="w-5 h-5 text-chocolate-milk shrink-0" />
                      <span>Learn the science behind perfect fudgy textures and layering</span>
                    </div>
                    <div className="flex items-center gap-2.5 text-cream-soft font-medium">
                      <Check className="w-5 h-5 text-chocolate-milk shrink-0" />
                      <span>Gluten-free & healthy millet-based options included</span>
                    </div>
                    <div className="flex items-center gap-2.5 text-cream-soft font-medium">
                      <Check className="w-5 h-5 text-chocolate-milk shrink-0" />
                      <span>Professional costing, menu building, and shelf-life blueprints</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <button 
                      onClick={() => setIsPopupOpen(true)}
                      data-testid="hero-cta-button"
                      className="px-8 py-[18px] bg-gradient-to-r from-[#5C3E30] to-[#2B1A12] text-chocolate-deep font-bold text-lg rounded-xl shadow-[0_10px_25px_rgba(43,26,18,0.08)] hover:shadow-[0_15px_35px_rgba(43,26,18,0.15)] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-center"
                    >
                      Reserve My Seat — Limited Spots Left
                    </button>
                    <a 
                      href="#showcase"
                      data-testid="hero-secondary-button"
                      className="px-8 py-[18px] bg-chocolate-elevated border border-chocolate-milk/25 text-cream-soft hover:text-cream-warm font-semibold text-lg rounded-xl hover:bg-chocolate-surface/40 transition-all duration-300 text-center"
                    >
                      Explore 12 Premium Flavours
                    </a>
                  </div>
                </div>

                {/* Right Side: High-converting hero card featuring Chef Ashmika's box */}
                <div className="lg:col-span-5 relative">
                  <div className="relative bg-chocolate-elevated border border-chocolate-milk/20 rounded-3xl p-6 shadow-[0_15px_40px_rgba(43,26,18,0.06)]">
                    
                    {/* Golden accent corners */}
                    <div className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-chocolate-milk/20" />
                    <div className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-chocolate-milk/20" />
                    <div className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-chocolate-milk/20" />
                    <div className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-chocolate-milk/20" />

                    <div className="overflow-hidden rounded-2xl relative group">
                      <img 
                        src="https://tagmango.com/assets/img7929-972d513c1877bca315a2be87bd772e7f.jpg"
                        alt="Chef Ashmika's Assorted Brownie and Blondie Luxury Box"
                        className="w-full h-80 object-cover object-center transform transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute top-4 left-4 bg-chocolate-deep/95 border border-chocolate-milk/30 px-3.5 py-1.5 rounded-full text-cream-warm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5">
                        <Star className="w-3.5 h-3.5 fill-gold-burnished text-gold-burnished" /> Chef's Curated Gifting Box
                      </div>
                    </div>

                    <div className="mt-6 space-y-4">
                      <div className="flex justify-between items-center text-sm border-b border-chocolate-milk/10 pb-2">
                        <span className="text-cream-soft font-semibold">Instructor:</span>
                        <span className="text-cream-warm font-bold">Chef Ashmika</span>
                      </div>
                      <div className="flex justify-between items-center text-sm border-b border-chocolate-milk/10 pb-2">
                        <span className="text-cream-soft font-semibold">Class Format:</span>
                        <span className="text-cream-warm font-bold">Online</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-cream-soft font-semibold">Bonuses Included:</span>
                        <span className="text-emerald-700 font-bold">Premium Sourcing & Recipe PDF</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </header>

          {/* 03. Social Proof & Media Badges */}
          <section className="bg-chocolate-elevated border-b border-chocolate-milk/10 py-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 text-center space-y-6">
              <p className="text-xs uppercase tracking-[0.25em] text-chocolate-milk font-bold">
                THE MARK OF HIGH CONFECTIONERY EXCELLENCE
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center justify-center">
                <div className="p-4 bg-chocolate-deep border border-chocolate-milk/10 flex flex-col items-center rounded-2xl shadow-sm">
                  <span className="text-2xl sm:text-3xl font-serif text-cream-warm font-bold">5,000+</span>
                  <span className="text-xs text-cream-soft font-semibold mt-1">Students Taught</span>
                </div>
                <div className="p-4 bg-chocolate-deep border border-chocolate-milk/10 flex flex-col items-center rounded-2xl shadow-sm">
                  <span className="text-2xl sm:text-3xl font-serif text-cream-warm font-bold">4.98 / 5</span>
                  <span className="text-xs text-cream-soft font-semibold mt-1">Star Satisfaction</span>
                </div>
                <div className="p-4 bg-chocolate-deep border border-chocolate-milk/10 flex flex-col items-center rounded-2xl shadow-sm">
                  <span className="text-2xl sm:text-3xl font-serif text-cream-warm font-bold">100%</span>
                  <span className="text-xs text-cream-soft font-semibold mt-1">Millet & GF Approved</span>
                </div>
                <div className="p-4 bg-chocolate-deep border border-chocolate-milk/10 flex flex-col items-center rounded-2xl shadow-sm">
                  <span className="text-2xl sm:text-3xl font-serif text-cream-warm font-bold">Lifetime</span>
                  <span className="text-xs text-cream-soft font-semibold mt-1">Class Access Support</span>
                </div>
              </div>
            </div>
          </section>

          {/* Countdown & Live Seating Banner */}
          <section className="py-12 bg-chocolate-deep border-b border-chocolate-milk/10">
            <div className="max-w-xl mx-auto px-4 text-center space-y-6">
              <h2 className="text-2xl sm:text-3xl font-serif tracking-tight text-cream-warm">
                The Masterclass Starts In
              </h2>
              
              <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
                <div className="bg-chocolate-elevated border border-chocolate-milk/15 p-3 rounded-2xl">
                  <span className="block text-3xl font-serif text-cream-warm font-bold">{timeLeft.hours.toString().padStart(2, "0")}</span>
                  <span className="text-xs text-cream-soft uppercase tracking-wider font-semibold">Hours</span>
                </div>
                <div className="bg-chocolate-elevated border border-chocolate-milk/15 p-3 rounded-2xl">
                  <span className="block text-3xl font-serif text-cream-warm font-bold">{timeLeft.minutes.toString().padStart(2, "0")}</span>
                  <span className="text-xs text-cream-soft uppercase tracking-wider font-semibold">Mins</span>
                </div>
                <div className="bg-chocolate-elevated border border-chocolate-milk/15 p-3 rounded-2xl">
                  <span className="block text-3xl font-serif text-cream-warm font-bold animate-pulse">{timeLeft.seconds.toString().padStart(2, "0")}</span>
                  <span className="text-xs text-cream-soft uppercase tracking-wider font-semibold">Secs</span>
                </div>
              </div>

              {/* Progress seat counter */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-cream-soft font-semibold">
                  <span>Capacity: 75 Dedicated Bakers</span>
                  <span className="text-chocolate-milk font-bold">Only 6 Seats Left!</span>
                </div>
                <div className="w-full bg-chocolate-surface rounded-full h-3 border border-chocolate-milk/10 overflow-hidden">
                  <div className="bg-gradient-to-r from-gold-burnished to-[#5C3E30] h-full w-[92%] rounded-full shadow-[0_0_10px_rgba(140,106,61,0.15)]" />
                </div>
              </div>
            </div>
          </section>

          {/* 06. What You'll Bake (Brownie vs Blondie Decadence Showcase Grid) - PLACED ABOVE STORIES AS REQUESTED */}
          <section id="showcase" className="py-16 lg:py-24 bg-chocolate-deep border-b border-chocolate-milk/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 text-center space-y-8">
              
              <div className="space-y-4 max-w-3xl mx-auto">
                <p className="text-xs uppercase tracking-[0.25em] text-chocolate-milk font-bold">
                  DECADENCE SHOWCASE
                </p>
                <h2 className="text-3xl sm:text-4xl font-serif text-cream-warm">
                  The 12 Signature Masterpiece Flavours
                </h2>
                <p className="text-cream-soft text-sm sm:text-base font-medium">
                  Explore our premium, dual-themed collections of 12 distinct flavours (6 Decadent Brownies & 6 Premium Blondies) designed to turn heads and delight your home bakery clients.
                </p>
              </div>

              {/* Tab Switcher */}
              <div className="flex justify-center gap-2 max-w-md mx-auto bg-chocolate-elevated p-1.5 rounded-xl border border-chocolate-milk/20">
                <button
                  onClick={() => setActiveTab("brownies")}
                  data-testid="showcase-tab-brownies"
                  className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all duration-300 ${
                    activeTab === "brownies"
                      ? "bg-gradient-to-r from-[#5C3E30] to-[#2B1A12] text-chocolate-deep shadow-md"
                      : "text-cream-soft hover:text-cream-warm"
                  }`}
                >
                  6 Decadent Brownies
                </button>
                <button
                  onClick={() => setActiveTab("blondies")}
                  data-testid="showcase-tab-blondies"
                  className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all duration-300 ${
                    activeTab === "blondies"
                      ? "bg-gradient-to-r from-[#5C3E30] to-[#2B1A12] text-chocolate-deep shadow-md"
                      : "text-cream-soft hover:text-cream-warm"
                  }`}
                >
                  6 Premium Blondies
                </button>
              </div>

              {/* Tab Contents Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-4">
                {activeTab === "brownies" ? (
                  <>
                    {/* Item 1 */}
                    <div className="bg-chocolate-elevated border border-chocolate-milk/15 rounded-2xl overflow-hidden hover:border-chocolate-milk/30 transition-all duration-300 group shadow-sm">
                      <div className="h-64 overflow-hidden relative">
                        <img 
                          src="https://tagmango.com/assets/copy-of-img8437-817e0299a3c4384fd9489f34ff0fc24e.jpg"
                          alt="Pistachio Baklava Brownie"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3 bg-chocolate-deep/95 text-chocolate-milk border border-chocolate-milk/20 text-xs px-2.5 py-1 rounded-full uppercase tracking-wider font-bold">
                          FUDGY LAYER
                        </div>
                      </div>
                      <div className="p-6 text-left space-y-2">
                        <h3 className="text-xl font-serif text-cream-warm">1. Pistachio Baklava Brownie</h3>
                        <p className="text-sm text-cream-soft leading-relaxed font-medium">
                          A heavenly combination of premium chocolate fudge, crispy buttered baklava phyllo layers, and rich roasted pistachio paste topped with burnished gold flakes.
                        </p>
                      </div>
                    </div>

                    {/* Item 2 */}
                    <div className="bg-chocolate-elevated border border-chocolate-milk/15 rounded-2xl overflow-hidden hover:border-chocolate-milk/30 transition-all duration-300 group shadow-sm">
                      <div className="h-64 overflow-hidden relative">
                        <img 
                          src="https://tagmango.com/assets/copy-of-img8436-82a0d4b7bbf21d10af69332dfdf24642.jpg"
                          alt="Dubai Pistachio Kunafa Brownie"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3 bg-chocolate-deep/95 text-chocolate-milk border border-chocolate-milk/20 text-xs px-2.5 py-1 rounded-full uppercase tracking-wider font-bold">
                          CRUNCHY INCLUSION
                        </div>
                      </div>
                      <div className="p-6 text-left space-y-2">
                        <h3 className="text-xl font-serif text-cream-warm">2. Dubai Pistachio Brownie</h3>
                        <p className="text-sm text-cream-soft leading-relaxed font-medium">
                          Inspired by the world-famous Dubai dessert, featuring a rich base of cocoa, crunchy toasted kunafa pastry, and a decadent, creamy pistachio-butter crown.
                        </p>
                      </div>
                    </div>

                    {/* Item 3 */}
                    <div className="bg-chocolate-elevated border border-chocolate-milk/15 rounded-2xl overflow-hidden hover:border-chocolate-milk/30 transition-all duration-300 group shadow-sm">
                      <div className="h-64 overflow-hidden relative">
                        <img 
                          src="https://tagmango.com/assets/img7931-a8fefd93642fd69b41c2685f08a49911.jpg"
                          alt="Zesty Orange Chocolate Brownie"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3 bg-chocolate-deep/95 text-chocolate-milk border border-chocolate-milk/20 text-xs px-2.5 py-1 rounded-full uppercase tracking-wider font-bold">
                          MILLET-BASED
                        </div>
                      </div>
                      <div className="p-6 text-left space-y-2">
                        <h3 className="text-xl font-serif text-cream-warm">3. Orange Chocolate Brownie</h3>
                        <p className="text-sm text-cream-soft leading-relaxed font-medium">
                          An exceptional combination of nutrient-rich millet flours, zesty candied orange peels, and dark chocolate ganache swirl that guarantees a perfect crumb structure.
                        </p>
                      </div>
                    </div>

                    {/* Additional Brownie list descriptions */}
                    <div className="col-span-1 md:col-span-2 lg:col-span-3 p-6 bg-chocolate-elevated border border-chocolate-milk/15 rounded-2xl flex flex-col md:flex-row justify-around items-start text-left gap-6 shadow-sm">
                      <div>
                        <h4 className="font-bold text-cream-warm text-base">4. Pistachio Kunafa Brownie</h4>
                        <p className="text-xs text-cream-soft mt-1 max-w-xs font-semibold">Double-layered crunchy Arabic pastry infusion inside rich dark cocoa batter.</p>
                      </div>
                      <div className="h-px md:h-12 w-full md:w-px bg-chocolate-milk/10" />
                      <div>
                        <h4 className="font-bold text-cream-warm text-base">5. Honey Salted Caramel Brownie</h4>
                        <p className="text-xs text-cream-soft mt-1 max-w-xs font-semibold">Housemade organic warm salted caramel ribbons folded beautifully inside gooey crumb.</p>
                      </div>
                      <div className="h-px md:h-12 w-full md:w-px bg-chocolate-milk/10" />
                      <div>
                        <h4 className="font-bold text-cream-warm text-base">6. Hazelnut Praline Brownie</h4>
                        <p className="text-xs text-cream-soft mt-1 max-w-xs font-semibold">Enriched with crunchy roasted hazelnuts and sweet chocolate nut crunch topping.</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Item 4 */}
                    <div className="bg-chocolate-elevated border border-chocolate-milk/15 rounded-2xl overflow-hidden hover:border-chocolate-milk/30 transition-all duration-300 group shadow-sm">
                      <div className="h-64 overflow-hidden relative">
                        <img 
                          src="https://tagmango.com/assets/copy-of-img8443-72c0d97f94e68135c76504cb18f85e47.jpg"
                          alt="Cookie brownie Blondie"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3 bg-chocolate-deep/95 text-chocolate-milk border border-chocolate-milk/20 text-xs px-2.5 py-1 rounded-full uppercase tracking-wider font-bold">
                          DOUBLE LAYERED
                        </div>
                      </div>
                      <div className="p-6 text-left space-y-2">
                        <h3 className="text-xl font-serif text-cream-warm">1. Cookie brownie Blondie</h3>
                        <p className="text-sm text-cream-soft leading-relaxed font-medium">
                          An indulgent multi-layered fusion bake combining fudgy chocolate brownie pieces and crispy chocolate-chip cookie crumbs swirled inside a rich vanilla blondie base.
                        </p>
                      </div>
                    </div>

                    {/* Item 5 */}
                    <div className="bg-chocolate-elevated border border-chocolate-milk/15 rounded-2xl overflow-hidden hover:border-chocolate-milk/30 transition-all duration-300 group shadow-sm">
                      <div className="h-64 overflow-hidden relative">
                        <img 
                          src="https://tagmango.com/assets/img7929-972d513c1877bca315a2be87bd772e7f.jpg"
                          alt="Rose & Pistachio Cashew Blondie"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3 bg-chocolate-deep/95 text-chocolate-milk border border-chocolate-milk/20 text-xs px-2.5 py-1 rounded-full uppercase tracking-wider font-bold">
                          DELIGHT Gifting
                        </div>
                      </div>
                      <div className="p-6 text-left space-y-2">
                        <h3 className="text-xl font-serif text-cream-warm">2. Rose & Pistachio Blondie</h3>
                        <p className="text-sm text-cream-soft leading-relaxed font-medium">
                          Featuring rich white chocolate-cashew blondie core infused with organic food-grade rosewater and topped with crushed green pistachios and dried edible rose petals.
                        </p>
                      </div>
                    </div>

                    {/* Item 6 */}
                    <div className="bg-chocolate-elevated border border-chocolate-milk/15 rounded-2xl overflow-hidden hover:border-chocolate-milk/30 transition-all duration-300 group shadow-sm">
                      <div className="h-64 overflow-hidden relative">
                        <img 
                          src="https://tagmango.com/assets/copy-of-img8436-82a0d4b7bbf21d10af69332dfdf24642.jpg"
                          alt="Single-Serve Gourmet Portions"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3 bg-chocolate-deep/95 text-chocolate-milk border border-chocolate-milk/20 text-xs px-2.5 py-1 rounded-full uppercase tracking-wider font-bold">
                          SINGLE-SERVE
                        </div>
                      </div>
                      <div className="p-6 text-left space-y-2">
                        <h3 className="text-xl font-serif text-cream-warm">3. Cashew White Chocolate Blondie</h3>
                        <p className="text-sm text-cream-soft leading-relaxed font-medium">
                          Individual portioned cashew butter-infused white chocolate blondie squares with high-end premium inclusions perfect for gifting.
                        </p>
                      </div>
                    </div>

                    {/* Additional Blondie list descriptions */}
                    <div className="col-span-1 md:col-span-2 lg:col-span-3 p-6 bg-chocolate-elevated border border-chocolate-milk/15 rounded-2xl flex flex-col md:flex-row justify-around items-start text-left gap-6 shadow-sm">
                      <div>
                        <h4 className="font-bold text-cream-warm text-base">4. Mixed Berry Blondie</h4>
                        <p className="text-xs text-cream-soft mt-1 max-w-xs font-semibold">Gooey base with stand-out organic berry compote swirl.</p>
                      </div>
                      <div className="h-px md:h-12 w-full md:w-px bg-chocolate-milk/10" />
                      <div>
                        <h4 className="font-bold text-cream-warm text-base">5. Single-Serve Blondies</h4>
                        <p className="text-xs text-cream-soft mt-1 max-w-xs font-semibold">Baked in standalone professional cavities for crispy edges on all four sides.</p>
                      </div>
                      <div className="h-px md:h-12 w-full md:w-px bg-chocolate-milk/10" />
                      <div>
                        <h4 className="font-bold text-cream-warm text-base">6. Biscoff Cookie Blondie</h4>
                        <p className="text-xs text-cream-soft mt-1 max-w-xs font-semibold">Creamy Biscoff cookie spread centers with speculoos biscuit inclusions baked inside.</p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="pt-6">
                <button
                  onClick={() => setIsPopupOpen(true)}
                  data-testid="showcase-cta-button"
                  className="px-8 py-[18px] bg-gradient-to-r from-[#5C3E30] to-[#2B1A12] text-chocolate-deep font-bold rounded-xl shadow-lg hover:shadow-[0_8px_20px_rgba(43,26,18,0.15)] hover:scale-[1.02] active:scale-[0.98] transition-all"
                >
                  Master These 12 Flavour Recipes Now
                </button>
              </div>

            </div>
          </section>

          {/* 04. Chef Ashmika's Story & Philosophy - PLACED BELOW SHOWCASE AS REQUESTED */}
          <section className="py-16 lg:py-24 bg-chocolate-elevated border-b border-chocolate-milk/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                
                <div className="space-y-6">
                  <p className="text-xs uppercase tracking-[0.25em] text-chocolate-milk font-bold">
                    MEET YOUR MASTER PASTRY CHEF
                  </p>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-cream-warm">
                    Meet Chef Ashmika
                  </h2>
                  <p className="text-cream-soft leading-relaxed font-medium">
                    With years of experience in top-tier patisseries, Chef Ashmika has dedicated her career to decoding the perfect chocolate textures. Now, she's bringing her signature layered methodologies to home bakers and professional chefs alike.
                  </p>
                  <blockquote className="border-l-2 border-chocolate-milk pl-4 italic text-cream-soft my-4 font-serif">
                    "My mission is to prove that baking with healthy millets and creating decadent, gourmet desserts are not mutually exclusive. I'll show you exactly how the chemical reactions of ingredients produce clean, luxurious layers that elevate your craft."
                  </blockquote>
                  <p className="text-cream-soft leading-relaxed font-medium">
                    Under her direct guidance, you'll learn not just recipes, but the underlying science of layered baking, temperature control, and moisture prevention, allowing you to confidently invent your own signatures.
                  </p>
                </div>

                {/* Chef display card with Image 5 */}
                <div className="relative">
                  <div className="relative overflow-hidden rounded-3xl border border-chocolate-milk/20 p-3 bg-chocolate-deep">
                    <img 
                      src="https://tagmango.com/assets/img7931-a8fefd93642fd69b41c2685f08a49911.jpg"
                      alt="Chef Ashmika detailing gourmet chocolate brownies"
                      className="w-full h-[450px] object-cover rounded-2xl"
                    />
                    <div className="absolute bottom-8 left-8 right-8 bg-chocolate-deep/95 border border-chocolate-milk/20 p-4 rounded-xl backdrop-blur-sm shadow-sm">
                      <p className="text-cream-warm font-bold text-lg">Chef Ashmika</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* 05. Interactive 6-Module Layered Curriculum (Bento Accordion) */}
          <section id="curriculum" className="py-16 lg:py-24 bg-chocolate-elevated border-b border-chocolate-milk/10">
            <div className="max-w-4xl mx-auto px-4 sm:px-8">
              
              <div className="text-center space-y-4 mb-12">
                <p className="text-xs uppercase tracking-[0.25em] text-chocolate-milk font-bold">
                  THE COMPREHENSIVE CURRICULUM
                </p>
                <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-cream-warm">
                  6 Modules of Pure Baking Science
                </h2>
                <p className="text-cream-soft text-base font-medium">
                  Click on each module below to reveal the detailed lessons, techniques, and recipes included.
                </p>
              </div>

              {/* Modules Accordion */}
              <div className="space-y-4">
                {curriculumModules.map((module, index) => {
                  const isOpen = expandedModule === index;
                  return (
                    <div 
                      key={index}
                      className="bg-chocolate-deep border border-chocolate-milk/15 rounded-2xl overflow-hidden transition-all duration-300 shadow-sm"
                    >
                      <button
                        onClick={() => setExpandedModule(isOpen ? -1 : index)}
                        data-testid={`curriculum-module-trigger-${index + 1}`}
                        className="w-full p-6 flex items-center justify-between gap-4 text-left hover:bg-chocolate-surface/40 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2.5 bg-chocolate-surface border border-chocolate-milk/15 rounded-xl">
                            {module.icon}
                          </div>
                          <h3 className="text-lg sm:text-xl font-serif text-cream-warm tracking-tight">
                            {module.title}
                          </h3>
                        </div>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-cream-soft shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-cream-soft shrink-0" />
                        )}
                      </button>

                      {/* Accordion Content */}
                      {isOpen && (
                        <div className="px-6 pb-6 pt-2 border-t border-chocolate-milk/10 bg-chocolate-deep/20">
                          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                            {module.bullets.map((bullet, bulletIdx) => (
                              <li key={bulletIdx} className="flex items-start gap-2.5 text-sm text-cream-soft font-semibold">
                                <Check className="w-4 h-4 text-chocolate-milk shrink-0 mt-0.5" />
                                <span>{bullet}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          </section>

          {/* What You Get Inclusions Box */}
          <section className="py-16 bg-chocolate-deep border-b border-chocolate-milk/10">
            <div className="max-w-4xl mx-auto px-4 sm:px-8">
              <div className="relative bg-chocolate-elevated border-2 border-chocolate-milk/20 rounded-3xl p-8 sm:p-12 shadow-[0_15px_40px_rgba(43,26,18,0.06)]">
                
                <div className="sm:absolute top-4 right-4 mb-4 sm:mb-0 inline-flex bg-chocolate-surface border border-chocolate-milk/20 px-3 py-1 rounded-full text-cream-soft text-xs uppercase tracking-widest font-bold items-center gap-1.5 w-fit">
                  <Gift className="w-4 h-4" /> INCLUDED FOR LIFETIME
                </div>

                <h2 className="text-2xl sm:text-3xl font-serif text-cream-warm mb-8">
                  Everything You Get in the Masterclass
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 border-b border-chocolate-milk/10 pb-8">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-chocolate-surface flex items-center justify-center border border-chocolate-milk/20 text-cream-soft shrink-0 shadow-sm">
                      <Check className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-cream-warm">Comprehensive Live Masterclass</h4>
                      <p className="text-xs text-cream-soft mt-1 font-semibold">Interactive step-by-step guidance directly with Chef Ashmika.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-chocolate-surface flex items-center justify-center border border-chocolate-milk/20 text-cream-soft shrink-0 shadow-sm">
                      <Check className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-cream-warm">Detailed Recipe PDF Blueprint</h4>
                      <p className="text-xs text-cream-soft mt-1 font-semibold">Gram-measurements, exact temperature profiles, and step blueprints.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-chocolate-surface flex items-center justify-center border border-chocolate-milk/20 text-cream-soft shrink-0 shadow-sm">
                      <Check className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-cream-warm">Lifetime Video Recordings</h4>
                      <p className="text-xs text-cream-soft mt-1 font-semibold">Watch and learn at your own pace anytime, with lifetime secure hosting.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-chocolate-surface flex items-center justify-center border border-chocolate-milk/20 text-cream-soft shrink-0 shadow-sm">
                      <Check className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-cream-warm">Lifetime Student Q&A Support</h4>
                      <p className="text-xs text-cream-soft mt-1 font-semibold">Direct feedback inside our private baker community channel.</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-2 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-chocolate-milk font-bold font-mono">Limited Class Registrations</p>
                    <p className="text-2xl sm:text-3xl font-serif text-cream-warm mt-1">
                      Exclusive Live Access Ticket Included
                    </p>
                  </div>
                  <button
                    onClick={() => setIsPopupOpen(true)}
                    data-testid="pricing-cta-button"
                    className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-[#5C3E30] to-[#2B1A12] text-chocolate-deep font-bold rounded-xl shadow-lg hover:shadow-[0_8px_20px_rgba(43,26,18,0.15)] transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Claim VIP Ticket Today
                  </button>
                </div>

              </div>
            </div>
          </section>

          {/* Verified Student Testimonials */}
          <section className="py-16 bg-chocolate-elevated border-b border-chocolate-milk/10">
            <div className="max-w-6xl mx-auto px-4 sm:px-8">
              
              <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
                <p className="text-xs uppercase tracking-[0.25em] text-chocolate-milk font-bold">
                  STUDENT MASTERPIECES
                </p>
                <h2 className="text-3xl sm:text-4xl font-serif text-cream-warm">
                  Praise from Home Bakers
                </h2>
                <p className="text-cream-soft font-medium">
                  Hundreds of graduates have built successful layered dessert ranges using our exact techniques.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Card 1 */}
                <div className="bg-chocolate-deep border border-chocolate-milk/15 p-6 rounded-2xl space-y-4 shadow-sm">
                  <div className="flex gap-1 text-gold-burnished">
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                  </div>
                  <p className="text-sm text-cream-soft leading-relaxed italic font-medium">
                    "Chef Ashmika's Baklava Brownie has literally transformed my home bakery sales! I was struggling to stand out, but this premium multi-textured layer is now our bestseller."
                  </p>
                  <div className="pt-2 border-t border-chocolate-milk/10">
                    <p className="text-xs font-bold text-cream-warm">Meera Sharma</p>
                    <p className="text-[10px] text-chocolate-milk font-bold">Home Baker, Meera's Sweet Oven</p>
                  </div>
                </div>

                {/* Card 2 */}
                <div className="bg-chocolate-deep border border-chocolate-milk/15 p-6 rounded-2xl space-y-4 shadow-sm">
                  <div className="flex gap-1 text-gold-burnished">
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                  </div>
                  <p className="text-sm text-cream-soft leading-relaxed italic font-medium">
                    "I was skeptical about millet-based baking, expecting dry crumbles. But the Orange Chocolate millet brownie was incredibly moist! My customers can't even tell it is healthy."
                  </p>
                  <div className="pt-2 border-t border-chocolate-milk/10">
                    <p className="text-xs font-bold text-cream-warm">Rohan Kapoor</p>
                    <p className="text-[10px] text-chocolate-milk font-bold">Chef and Café Consultant</p>
                  </div>
                </div>

                {/* Card 3 */}
                <div className="bg-chocolate-deep border border-chocolate-milk/15 p-6 rounded-2xl space-y-4 shadow-sm">
                  <div className="flex gap-1 text-gold-burnished">
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                  </div>
                  <p className="text-sm text-cream-soft leading-relaxed italic font-medium">
                    "The single-serve portioning lessons were gold! It made boxed catering orders for corporate gifting so clean and standardized. Highly recommend!"
                  </p>
                  <div className="pt-2 border-t border-chocolate-milk/10">
                    <p className="text-xs font-bold text-cream-warm">Ananya Bose</p>
                    <p className="text-[10px] text-chocolate-milk font-bold">Founder, Petite Pâtisserie</p>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* 11. Frequently Asked Questions */}
          <section className="py-16 lg:py-24 bg-chocolate-deep border-b border-chocolate-milk/10">
            <div className="max-w-3xl mx-auto px-4 sm:px-8">
              
              <div className="text-center space-y-4 mb-12">
                <p className="text-xs uppercase tracking-[0.25em] text-chocolate-milk font-bold">
                  HAVE QUESTIONS?
                </p>
                <h2 className="text-3xl sm:text-4xl font-serif text-cream-warm">
                  Frequently Asked Questions
                </h2>
              </div>

              <div className="space-y-4">
                {faqList.map((faq, index) => {
                  const isFaqOpen = expandedFaq === index;
                  return (
                    <div 
                      key={index}
                      className="bg-chocolate-elevated border border-chocolate-milk/10 rounded-xl overflow-hidden shadow-sm"
                    >
                      <button
                        onClick={() => setExpandedFaq(isFaqOpen ? null : index)}
                        data-testid={`faq-trigger-${index + 1}`}
                        className="w-full p-5 flex items-center justify-between gap-4 text-left hover:bg-chocolate-surface/30 transition-all"
                      >
                        <span className="font-bold text-cream-warm text-base">
                          {faq.question}
                        </span>
                        {isFaqOpen ? (
                          <ChevronUp className="w-5 h-5 text-cream-soft shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-cream-soft shrink-0" />
                        )}
                      </button>

                      {isFaqOpen && (
                        <div className="p-5 border-t border-chocolate-milk/10 bg-chocolate-deep/20 text-cream-soft text-sm leading-relaxed font-semibold">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          </section>

          {/* Footer Branding Area */}
          <footer className="py-12 bg-chocolate-deep text-center text-xs text-chocolate-milk border-t border-chocolate-milk/10 space-y-4">
            <div className="max-w-7xl mx-auto px-4 animate-fade-in font-semibold">
              <p className="text-sm font-serif text-cream-warm">
                Chef Ashmika's Mango Studio
              </p>
              <p className="mt-1">
                Branded domain: <a href="https://dashboard.chefashmika.com" className="hover:underline">dashboard.chefashmika.com</a>
              </p>
              <p className="mt-4 text-[10px] text-cream-soft">
                © {new Date().getFullYear()} Mango AI. All rights reserved. Created for professional bakers under TagMango Studio.
              </p>
            </div>
          </footer>

          {/* 13. Sticky Funnel Footer */}
          {showStickyFooter && (
            <div 
              data-testid="sticky-footer"
              className="fixed bottom-0 left-0 right-0 bg-chocolate-elevated/95 border-t border-chocolate-milk/30 py-3.5 px-4 z-40 backdrop-blur-md shadow-[0_-10px_30px_rgba(43,26,18,0.06)] transition-all duration-500"
            >
              <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-chocolate-milk uppercase tracking-widest font-bold">LIMITED TIME REGISTRATION</p>
                  <p className="text-lg font-serif text-cream-warm">
                    Live Session Access Included
                    <span className="text-xs text-emerald-700 font-sans ml-2.5 font-bold">• Only 6 seats remaining!</span>
                  </p>
                </div>
                <button
                  onClick={() => setIsPopupOpen(true)}
                  data-testid="sticky-footer-cta"
                  className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-[#5C3E30] to-[#2B1A12] text-chocolate-deep font-bold text-sm rounded-lg hover:shadow-[0_8px_15px_rgba(43,26,18,0.15)] transition-all uppercase tracking-wider"
                >
                  Reserve Spot Now
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* 10. Form Popup Modal (Framer-Motion style custom overlay) */}
      {isPopupOpen && (
        <div 
          onClick={() => setIsPopupOpen(false)} // Clicking backdrop closes modal
          className="fixed inset-0 bg-chocolate-deep/85 backdrop-blur-md z-50 flex items-center justify-center p-4 transition-all duration-300"
        >
          <div 
            onClick={(e) => e.stopPropagation()} // Prevent close on clicking inner card
            className="max-w-md w-full bg-chocolate-elevated border-2 border-chocolate-milk/30 rounded-3xl p-6 sm:p-8 shadow-[0_15px_45px_rgba(43,26,18,0.08)] relative overflow-hidden"
          >
            
            {/* Modal close button */}
            <button
              onClick={() => setIsPopupOpen(false)}
              data-testid="registration-close-button"
              className="absolute top-4 right-4 p-1.5 rounded-full bg-chocolate-surface hover:bg-chocolate-cocoa text-cream-soft hover:text-cream-warm transition-all border border-chocolate-milk/15 z-30"
              aria-label="Close form"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Glowing gold border elements */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#5C3E30] to-[#2B1A12]" />

            <div className="space-y-4 relative z-10">
              <div className="text-center space-y-1 sm:pt-2 pr-6">
                <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-chocolate-milk font-bold">STEP 1 of 2: RESERVATION DETAILS</p>
                <h3 className="text-xl sm:text-2xl font-serif text-cream-warm tracking-tight">
                  Secure Your Masterclass Seat
                </h3>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-4 pt-2">
                {/* Full Name Input */}
                <div className="space-y-1.5">
                  <label className="text-xs text-cream-soft font-bold uppercase tracking-wider block">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your first & last name"
                    required
                    data-testid="registration-input-name"
                    className="w-full bg-chocolate-deep border border-chocolate-milk/20 rounded-xl px-4 py-3 text-cream-warm placeholder:text-muted-amber focus:outline-none focus:border-chocolate-milk focus:ring-1 focus:ring-chocolate-milk text-sm transition-all shadow-sm"
                  />
                </div>

                {/* WhatsApp Number Input */}
                <div className="space-y-1.5">
                  <label className="text-xs text-cream-soft font-bold uppercase tracking-wider block">
                    WhatsApp Number
                  </label>
                  <input
                    type="tel"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleInputChange}
                    placeholder="e.g. 9876543210 (10-12 digits)"
                    required
                    pattern={"[0-9+\\(\\) \\-]{10,20}"}
                    data-testid="registration-input-whatsapp"
                    className="w-full bg-chocolate-deep border border-chocolate-milk/20 rounded-xl px-4 py-3 text-cream-warm placeholder:text-muted-amber focus:outline-none focus:border-chocolate-milk focus:ring-1 focus:ring-chocolate-milk text-sm transition-all shadow-sm"
                  />
                </div>

                {/* Email Input */}
                <div className="space-y-1.5">
                  <label className="text-xs text-cream-soft font-bold uppercase tracking-wider block">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="e.g. delicious@baker.com"
                    required
                    data-testid="registration-input-email"
                    className="w-full bg-chocolate-deep border border-chocolate-milk/20 rounded-xl px-4 py-3 text-cream-warm placeholder:text-muted-amber focus:outline-none focus:border-chocolate-milk focus:ring-1 focus:ring-chocolate-milk text-sm transition-all shadow-sm"
                  />
                </div>

                {/* Secure Badge Info */}
                <div className="p-3 bg-chocolate-deep border border-chocolate-milk/10 rounded-xl flex gap-2.5 items-start">
                  <Lock className="w-4 h-4 text-chocolate-milk shrink-0 mt-0.5" />
                  <p className="text-[10px] text-cream-soft leading-relaxed font-semibold">
                    <strong>Zero Spam Guarantee:</strong> We only use your information for masterclass invite links, recipe PDFs, and community support notifications. Your contact details remain 100% private.
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  data-testid="registration-submit-button"
                  className="w-full py-[18px] bg-gradient-to-r from-[#5C3E30] to-[#2B1A12] text-chocolate-deep font-bold text-base rounded-xl shadow-[0_8px_20px_rgba(43,26,18,0.12)] hover:shadow-[0_12px_28px_rgba(43,26,18,0.2)] transition-all hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
                >
                  Confirm Spot & Get WhatsApp Invite <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
